<template>
    <a class="indexpage-news-line" v-if="headlines.length > 0"
        v-bind:href="current.link"
        target="_blank"
        rel="noopener">
        <icon-newspaper class="indexpage-news-line__icon" />
        <span class="indexpage-news-line__viewport">
            <transition name="indexpage-news-line-flip">
                <span class="indexpage-news-line__text" v-bind:key="current.link">
                    {{ current.text }}
                </span>
            </transition>
        </span>
    </a>
    <span class="indexpage-news-line" v-else>
        <span class="skeleton" style="width: 140px;">test</span>
    </span>
</template>

<script>
import { getNews } from '~/api/news';
import { NEWS_CHANNEL_IDS } from '~/components/widgets/NewsFeed/channels.js';
import IconNewspaper from '@img/icons/lucide/newspaper.svg?vue';

const HEADLINE_COUNT = 4;
const FLIP_INTERVAL_MS = 4000;

/**
 * Posts come as html; the first line of its text is the headline, the css clips it to the room left beside the price.
 * @param  {String} html
 * @return {String}
 */
const makeHeadline = function (html) {
    const container = document.createElement('div');
    container.innerHTML = html.replace(/<br\s*\/?>/gi, '\n');

    // Posts tend to open with an emoji, the newspaper icon stands in for it:
    const firstLine = (container.textContent || '')
        .split('\n')
        .map(line => line.replace(/^[^\p{L}\p{N}$«"'(]+/u, '').trim())
        .find(line => line.length > 0);

    return firstLine || '';
};

export default {
    data() {
        return {
            headlines: [],
            index: 0,
        };
    },

    computed: {
        current() {
            return this.headlines[this.index % this.headlines.length];
        },
    },

    watch: {
        '$i18n.locale': function () {
            this.loadHeadlines();
        },
    },

    mounted() {
        this.loadHeadlines();
    },

    beforeDestroy() {
        clearInterval(this.flipTimer);
    },

    methods: {
        async loadHeadlines() {
            clearInterval(this.flipTimer);
            this.headlines = [];
            this.index = 0;

            try {
                const { channels, posts } = await getNews(NEWS_CHANNEL_IDS[this.$i18n.locale], 12);

                this.headlines = posts
                    .map((post) => ({
                        text: post.text ? makeHeadline(post.text) : '',
                        link: `https://t.me/${channels[post.channel_id].username}/${post.post_id}`,
                    }))
                    .filter(({ text }) => text.length > 0)
                    .slice(0, HEADLINE_COUNT);
            } catch {
                // Third-party news api, a failure leaves the skeleton in place.
                return;
            }

            if (this.headlines.length > 1) {
                this.flipTimer = setInterval(() => { this.index += 1; }, FLIP_INTERVAL_MS);
            }
        },
    },

    components: { IconNewspaper },
};
</script>

<style lang="scss">
.indexpage-news-line {
    display: flex;
    align-items: center;
    min-width: 0;
    margin-left: auto;
    gap: 6px;
    color: var(--body-muted-text-color);
    text-decoration: none;

    &:hover {
        text-decoration: none;
        color: var(--body-muted-text-color);
    }

    &__icon {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        stroke-width: 1.8;
    }

    // The frame the headline flips inside, the leaving line and the arriving one
    // share the same grid cell. The frame takes whatever room is left beside the price
    // and the text is clipped with an ellipsis, so the gap to the price never closes:
    &__viewport {
        display: grid;
        min-width: 0;
        height: 1.4em;
        overflow: hidden;
        perspective: 200px;
    }

    &__text {
        grid-area: 1 / 1;
        min-width: 0;
        line-height: 1.4em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        transform-origin: 50% 50%;
        backface-visibility: hidden;
    }
}

.indexpage-news-line-flip-enter-active,
.indexpage-news-line-flip-leave-active {
    transition: transform 0.45s ease, opacity 0.45s ease;
}

.indexpage-news-line-flip-enter {
    transform: translateY(60%) rotateX(-80deg);
    opacity: 0;
}

.indexpage-news-line-flip-leave-to {
    transform: translateY(-60%) rotateX(80deg);
    opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
    .indexpage-news-line-flip-enter,
    .indexpage-news-line-flip-leave-to {
        transform: none;
    }
}
</style>
